/**
 * Funding types for React Native admin app
 * Matches the admin-web funding types exactly
 */

export enum IFundingActions {
  reprocess = "reprocess",
  download = "download",
}

export enum FundingKeys {
  property = "property",
  merchant_id = "merchantId",
  funding_date = "fundingDate",
  last_updated = "last_updated",
  last_status = "lastStatus",
  funding_amount = "funding_amount",
  fee_amount = "fee_amount",
  net_amount = "net_amount",
  actions = "actions",
}

export interface IPropertyDetail {
  id: number;
  name: string;
  merchantIds: string[];
}

export interface IRowData {
  property: string;
  merchant_id: string;
  funding_date: string;
  last_updated: string;
  last_status: string;
  funding_amount: number;
  fee_amount: number;
  net_amount: number;
  actions: IFundingActions[];
}

export interface GLDetail {
  creditAmount: string;
  debitAmount: string;
  glAccountId: number;
  memo: string;
  propertyId: number;
}

export interface FundingJournalData {
  propertyId: number;
  glBookId: number;
  reference: string;
  postDate: string;
  postMonth: string;
  notes: string;
  glDetail: GLDetail[];
}

// Batch interface to replace any[]
export interface IBatch {
  batchId: string;
  amount: number;
  transactionCount: number;
  status: string;
  date: string;
}

export interface Funding {
  id: number;
  property: string;
  merchantId: string;
  fundingId: null | number;
  batches: IBatch[]; // Properly typed batches instead of any[]
  fundingDate: string; // API uses fundingDate
  transactionId: null | number;
  numFundings: number; // API uses numFundings, not fundings
  lastStatus: string;
  createdAt: string;
  updatedAt: string;
  fundingJournalData: null | FundingJournalData;
}

export interface FundingList {
  rows: Funding[];
  count: number;
}

// Table row interface for React Native display
export interface IFundingTableRow {
  id: number;
  property: string;
  merchantId: string;
  fundingDate: Date;
  lastStatus: string;
  funding_amount: FundingJournalData | null;
  fee_amount: FundingJournalData | null;
  net_amount: FundingJournalData | null;
  actions: number; // funding ID for actions
}

// Filter model interface (matching existing pattern)
export interface IFundingFilterModel {
  items: IFundingFilterItem[];
}

export interface IFundingFilterItem {
  field: string;
  operator: string;
  value: string | string[];
}

// Sort model interface (matching existing pattern)
export interface IFundingSortModel {
  field: string;
  sort: 'asc' | 'desc';
}

// Pagination model interface (matching existing pattern)
export interface IFundingPaginationModel {
  page: number;
  pageSize: number;
}

// API request parameters
export interface IFundingListRequest {
  sortModel: IFundingSortModel[];
  filterModel: IFundingFilterModel;
  paginationModel: IFundingPaginationModel;
  selectedOrganizationId: number;
}

// API response interface (matches actual API structure)
export interface IFundingListResponse {
  count: number;
  rows: Funding[];
}

// Full API response wrapper (what the API actually returns)
export interface IFundingApiResponse {
  success: boolean;
  response: IFundingListResponse;
}

// Reprocess funding request
export interface IReprocessFundingRequest {
  ids: number[];
  organizationId: number;
}

// Enhanced amounts interface for display
export interface IFundingAmounts {
  debitAmount: number;
  creditAmount: number;
}

// ===== API RESPONSE TYPES =====

// Reprocess funding response (based on backend controller analysis)
export interface IReprocessFundingResponse {
  success: boolean;
  response: IProcessedFunding[];
}

export interface IProcessedFunding {
  id: number;
  fundingId: string | null;
  fundingDate: string;
  lastStatus: string;
  extTransactionId: string | null;
  fundingData: IFundingData | null;
  merchantAccount: IMerchantAccountWithProperty;
}

export interface IFundingData {
  txns: ITransaction[];
  fundings: IFundingDetail[];
  fundingdate: string;
  merchid: string;
  fundingmasterid?: string;
}

export interface ITransaction {
  retref: string;
  batchid: string;
  amount: string;
  type: string;
  status: string;
  date?: string;
  achreturncode?: string;
}

export interface IFundingDetail {
  fundingid: string;
  fundingmasterid: string;
  totalfunding: string;
  netsales: string;
}

// Organization API response with property details
export interface IOrganizationWithPropertiesResponse {
  success: boolean;
  response: {
    id: number;
    name: string;
    properties: IPropertyWithMerchants[];
  };
}

export interface IPropertyWithMerchants {
  id: number;
  marketingName: string;
  externalId: string;
  merchantAccounts: IMerchantAccountInfo[];
}

export interface IMerchantAccountInfo {
  id: number;
  merchantId: string;
  isPrimary: boolean;
}

export interface IMerchantAccountWithProperty {
  id: number;
  merchantId: string;
  isPrimary: boolean;
  property: {
    id: number;
    marketingName: string;
    externalId: string;
    organizationId: number;
  };
}

// ===== CSV DATA TYPES =====

export interface IFundingCsvData {
  rows: IFundingCsvRow[];
  count: number;
}

export interface IFundingCsvRow {
  property: string;
  merchantId: string;
  fundingDate: string;
  lastStatus: string;
  fundingJournalData: FundingJournalData | null;
}

// ===== UTILITY TYPES =====

export type CsvRow = (string | number)[];
export type CsvData = CsvRow[];

// ===== TYPE GUARDS =====

export function isFundingJournalData(data: unknown): data is FundingJournalData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'glDetail' in data &&
    Array.isArray((data as FundingJournalData).glDetail)
  );
}

export function isGLDetail(detail: unknown): detail is GLDetail {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    'debitAmount' in detail &&
    'creditAmount' in detail &&
    'memo' in detail
  );
}

export function isValidFundingRow(row: unknown): row is IFundingCsvRow {
  return (
    typeof row === 'object' &&
    row !== null &&
    'property' in row &&
    'merchantId' in row &&
    'fundingDate' in row &&
    'lastStatus' in row
  );
}

// ===== HANDLER TYPES =====

export type BulkReprocessHandler = (updatedFundings: IProcessedFunding[]) => void;
export type SingleReprocessHandler = (updatedFunding: IProcessedFunding) => void;
